<template>
  <div>
    <invoices-header />

    <payments-invoices-table
      :invoices="data.invoices"
      :loading="data.loading"
      :pagination="data.pagination"
      @on-mark-as-paid="methods.handleMarkAsPaid"
      @on-pagination-change="methods.getInvoicesData"
    />

    <payments-invoices-mark-as-paid-modal
      v-if="data.isModalMarkAsPaidOpen && data.invoiceSelected"
      :invoice="data.invoiceSelected"
      :loading="data.markAsPaidLoading"
      @on-mark-as-paid="methods.markAsPaid"
      @close="data.isModalMarkAsPaidOpen = false"
    />
  </div>
</template>

<script setup lang="ts">
import PaymentsInvoicesMarkAsPaidModal from '@/components/modals/PaymentsInvoicesMarkAsPaidModal.vue'
import PaymentsInvoicesTable from '@/components/tables/PaymentsInvoicesTable.vue'
import InvoicesHeader from '@/components/headers/InvoicesHeader.vue'
import state from '@/state'
import { useToast } from '@wallbox/toolkit-ui'
import { trackDataAction } from '@/engine/metrics/trackDataManager'
import { reactive, watch } from 'vue'
import { useI18n } from '@/hooks/useI18n.hook'
import { useRouter } from 'vue-router'
import { INVOICES_USE_CASES, injectStrict } from '@/engine/injectors'
import type { PaymentsInvoice } from '@/core/invoices'
import type { FiltersQuery } from '@/core/shared'

const invoicesUseCases = injectStrict(INVOICES_USE_CASES)
const router = useRouter()
const i18n = useI18n()
const toast = useToast()

interface Data {
  loading: boolean,
  invoices: PaymentsInvoice[],
  filtersParams: Array<FiltersQuery>
  isModalMarkAsPaidOpen: boolean
  markAsPaidLoading: boolean,
  invoiceSelected?: PaymentsInvoice,
  pagination: {
    offset: number,
    limit: number
    total: number
    multiPage: boolean
  }
}
const data = reactive<Data>({
  loading: true,
  invoices: [],
  filtersParams: [],
  isModalMarkAsPaidOpen: false,
  markAsPaidLoading: false,
  pagination: {
    offset: 0,
    limit: 25,
    total: 0,
    multiPage: true
  }
})

async function created () {
  router.push({ query: {} }).catch(() => {})
}

const methods = {
  async getInvoicesData (offset = 0) {
    data.loading = true
    data.invoices = []

    const groupUid = state.organizations.getCurrentOrganization.groupUid
    data.pagination.offset = offset

    const invoices = await invoicesUseCases.getAllPaymentsInvoices(groupUid, {
      limit: data.pagination.limit,
      offset: data.pagination.offset,
      filters: state.filters.invoicesBackendFilters
    })

    data.invoices = invoices.data
    data.pagination.total = invoices.total
    data.loading = false
  },

  handleMarkAsPaid (invoice: PaymentsInvoice) {
    data.invoiceSelected = invoice
    data.isModalMarkAsPaidOpen = true
  },

  async markAsPaid (invoice: PaymentsInvoice) {
    data.markAsPaidLoading = true
    try {
      await invoicesUseCases.payPaymentsInvoice(invoice.id)
      await methods.getInvoicesData()
      toast.success(i18n.t('mywb.common.changes-saved'))
      trackDataAction('invoice-paid')
    } catch {
      toast.error(i18n.t('mywb.error.unexpected-error'))
    } finally {
      data.markAsPaidLoading = false
      data.isModalMarkAsPaidOpen = false
    }
  }
}

created()

watch(() => state.filters.invoicesBackendFilters, () => {
  data.pagination.offset = 0
  methods.getInvoicesData()
}, { immediate: true, deep: true })
</script>
